import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Toaster as SonnerToaster } from 'sonner';
import DesktopNavbar from './DesktopNavbar';
import MobileTopHeader from './MobileTopHeader';
import MobileBottomNav from './MobileBottomNav';
import MobileMoreDrawer from './MobileMoreDrawer';
import FeedStaleBanner from '../FeedStaleBanner';
import RecoveryEmailBanner from '../RecoveryEmailBanner';
import BayesianGatePill from './BayesianGatePill';
import useViewportWidth from '@/hooks/useViewportWidth';

/**
 * AppShell — wraps every authenticated route with the global navigation
 * (sticky desktop navbar OR mobile top header + bottom nav + drawer).
 *
 * Tier breakpoints (tablet upgrade 2026-02-11):
 *   Mobile           : vpw < 600
 *   Tablet portrait  : 600–899  → uses MOBILE chrome (top header +
 *                                  bottom-nav + More drawer) for
 *                                  thumb reach and zero overflow risk.
 *   Tablet landscape : 900–1180 → uses DesktopNavbar with its
 *                                  existing graceful demotion ladder.
 *   Desktop          : ≥1181
 */
export default function AppShell({ children }) {
  const vpw = useViewportWidth();
  // Tablet portrait + small mobile both use the mobile chrome — gives
  // us hamburger + bottom-nav out of the box, no overflow risk, and
  // tap targets sized correctly.
  const useMobileChrome = vpw < 900;
  const [moreOpen, setMoreOpen] = useState(false);
  // Pages that render their OWN primary nav (e.g. DashboardV2's
  // left vertical sidebar) suppress the global DesktopNavbar to
  // avoid double-chrome. Mobile path keeps the mobile header.
  const { pathname } = useLocation();
  const suppressDesktopNavbar = (
    pathname === '/' ||
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard-v2')
  );

  // Allow legacy code paths to dispatch the open event (kept for
  // backward-compat with any existing testIDs that listen for it).
  useEffect(() => {
    const onOpen = () => setMoreOpen(true);
    window.addEventListener('apexflow:open-overflow', onOpen);
    return () =>
      window.removeEventListener('apexflow:open-overflow', onOpen);
  }, []);

  return (
    <div data-testid="app-shell">
      <FeedStaleBanner />
      <RecoveryEmailBanner />
      <BayesianGatePill />
      {useMobileChrome
        ? <MobileTopHeader />
        : (suppressDesktopNavbar ? null : <DesktopNavbar />)}
      {children}
      {useMobileChrome && (
        <>
          <MobileBottomNav onOpenMore={() => setMoreOpen(true)} />
          <MobileMoreDrawer open={moreOpen}
                            onClose={() => setMoreOpen(false)} />
        </>
      )}
      <SonnerToaster
        position="top-right"
        richColors
        closeButton
        theme="dark" />
    </div>
  );
}
